import { useState, useMemo } from "react";
import { useRole } from "../../contexts/RoleContext";
import { AdminDashboard } from "./dashboard/AdminDashboard";
import { DoctorDashboard } from "./dashboard/DoctorDashboard";
import { DepartmentHeadDashboard } from "./dashboard/DepartmentHeadDashboard";
import { Activity, Calendar } from "lucide-react";
import { DateRangeToggle } from "../../components/admin/dashboard/DateRangeToggle";
import { RoleSelector } from "../../components/admin/dashboard/RoleSelector";

function DashboardRouter({ dateRange }: { dateRange: "today" | "week" }) {
  const { role } = useRole();

  switch (role) {
    case "admin":
      return <AdminDashboard dateRange={dateRange} />;
    case "doctor":
      return <DoctorDashboard dateRange={dateRange} />;
    case "nurse":
      return <DoctorDashboard dateRange={dateRange} />;
    case "department_head":
      return <DepartmentHeadDashboard dateRange={dateRange} />;
    default:
      return <AdminDashboard dateRange={dateRange} />;
  }
}

interface HeaderProps {
  dateRange: "today" | "week";
  onDateRangeChange: (range: "today" | "week") => void;
}

function Header({ dateRange, onDateRangeChange }: HeaderProps) {
  const today = new Date();
  const formattedDate = today.toLocaleDateString('vi-VN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <header className="sticky top-0 z-50 bg-background border-b border-border">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between gap-4 h-16">
          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground leading-tight">Dashboard</h1>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{formattedDate}</span>
          </div>

          <div className="flex items-center gap-3 flex-wrap justify-end">
            <DateRangeToggle value={dateRange} onChange={onDateRangeChange} />
            <RoleSelector />
          </div>
        </div>
      </div>
    </header>
  );
}

export default function DashboardPage() {
  const [dateRange, setDateRange] = useState<"today" | "week">("today");

  return (
    <div className="min-h-screen bg-background">
      <Header dateRange={dateRange} onDateRangeChange={setDateRange} />
      <main className="max-w-screen-2xl mx-auto px-4 md:px-8 py-6 md:py-8">
        <DashboardRouter dateRange={dateRange} />
      </main>
    </div>
  );
}
